import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { FilesystemService } from 'src/core/filesystem/domain/filesystem.service';
import { SessionService } from 'src/core/session/domain/session.service';
import { Nullable } from '../../common/domain/types/types';
import {
  FileDownloadOptions,
  FileDownloadProgress,
  FileDownloadStatuses,
  FileDownloadTask,
} from './file-download.types';
import { LocalFileStore } from './local-file-store';
import { ApiService } from '../../api/domain/api.service';
import { SystemService } from '../../system/system.service';

@Injectable({
  providedIn: 'root',
})
export class FileDownloadService {
  private static readonly DEFAULT_TIMEOUT_IN_MILLISECONDS = 30_000;

  constructor(
    private readonly filesystem: FilesystemService,
    private readonly localFileStore: LocalFileStore,
    private readonly session: SessionService,
    private readonly apiService: ApiService
  ) {}

  downloadToAttachment(url: string, destinationFolder: string, options?: FileDownloadOptions): FileDownloadTask {
    let xhr: Nullable<XMLHttpRequest> = new XMLHttpRequest();
    let cancelled = false;

    const progress$ = new Observable<FileDownloadProgress>((observer) => {
      const timeoutInMilliseconds =
        options?.timeoutInMilliseconds ?? FileDownloadService.DEFAULT_TIMEOUT_IN_MILLISECONDS;

      let resolvedFilename: Nullable<string> = null;
      let resolvedMimeType: Nullable<string> = null;

      observer.next({
        status: FileDownloadStatuses.Pending,
        loadedBytes: 0,
        totalBytes: null,
        progressPercentage: 0,
        fileName: null,
        mimeType: null,
        attachment: null,
        error: null,
      });

      xhr.open('GET', url, true);
      xhr.responseType = 'blob';
      xhr.timeout = timeoutInMilliseconds;

      const accessToken = this.session.getAccessToken();
      xhr.setRequestHeader('Authorization', `Bearer ${accessToken.value}`);

      const authToken = this.session.getAuthToken();
      if (authToken !== null) {
        xhr.setRequestHeader('X-Auth-Token', authToken.value);
      }

      xhr.onprogress = (event: ProgressEvent<EventTarget>) => {
        const totalBytes = event.lengthComputable ? event.total : null;
        const progressPercentage =
          totalBytes !== null && totalBytes > 0 ? Math.round((event.loaded / totalBytes) * 100) : null;

        observer.next({
          status: FileDownloadStatuses.Downloading,
          loadedBytes: event.loaded,
          totalBytes,
          progressPercentage,
          fileName: resolvedFilename,
          mimeType: resolvedMimeType,
          attachment: null,
          error: null,
        });
      };

      xhr.onload = async () => {
        try {
          if (cancelled) {
            observer.next({
              status: FileDownloadStatuses.Cancelled,
              loadedBytes: 0,
              totalBytes: null,
              progressPercentage: null,
              fileName: resolvedFilename,
              mimeType: resolvedMimeType,
              attachment: null,
              error: null,
            });
            observer.complete();
            return;
          }

          if (xhr.status < 200 || xhr.status >= 300) {
            throw new Error(`Download failed with status ${xhr.status}`);
          }

          const blob = xhr.response as Blob;

          if (!(blob instanceof Blob)) {
            throw new Error('Response is not a valid Blob');
          }

          resolvedMimeType = blob.type || null;

          if (options?.maxBytes !== undefined && options.maxBytes !== null && blob.size > options.maxBytes) {
            throw new Error(`File exceeds the maximum allowed size of ${options.maxBytes} bytes`);
          }

          resolvedFilename = await this.resolveFilename(url, xhr, destinationFolder, options);

          observer.next({
            status: FileDownloadStatuses.Saving,
            loadedBytes: blob.size,
            totalBytes: blob.size,
            progressPercentage: 100,
            fileName: resolvedFilename,
            mimeType: resolvedMimeType,
            attachment: null,
            error: null,
          });

          await this.localFileStore.saveBlob(destinationFolder, resolvedFilename, blob);

          observer.next({
            status: FileDownloadStatuses.Completed,
            loadedBytes: blob.size,
            totalBytes: blob.size,
            progressPercentage: 100,
            fileName: resolvedFilename,
            mimeType: resolvedMimeType,
            attachment: null,
            error: null,
          });

          observer.complete();
        } catch (error) {
          observer.next({
            status: FileDownloadStatuses.Error,
            loadedBytes: 0,
            totalBytes: null,
            progressPercentage: null,
            fileName: resolvedFilename,
            mimeType: resolvedMimeType,
            attachment: null,
            error,
          });

          observer.error(error);
        }
      };

      xhr.onerror = () => {
        const error = new Error('Network error while downloading file');

        observer.next({
          status: cancelled ? FileDownloadStatuses.Cancelled : FileDownloadStatuses.Error,
          loadedBytes: 0,
          totalBytes: null,
          progressPercentage: null,
          fileName: resolvedFilename,
          mimeType: resolvedMimeType,
          attachment: null,
          error: cancelled ? null : error,
        });

        if (cancelled) {
          observer.complete();
          return;
        }

        observer.error(error);
      };

      xhr.ontimeout = () => {
        const error = new Error(`Download timeout after ${timeoutInMilliseconds} ms`);

        observer.next({
          status: FileDownloadStatuses.Error,
          loadedBytes: 0,
          totalBytes: null,
          progressPercentage: null,
          fileName: resolvedFilename,
          mimeType: resolvedMimeType,
          attachment: null,
          error,
        });

        observer.error(error);
      };

      xhr.onabort = () => {
        observer.next({
          status: FileDownloadStatuses.Cancelled,
          loadedBytes: 0,
          totalBytes: null,
          progressPercentage: null,
          fileName: resolvedFilename,
          mimeType: resolvedMimeType,
          attachment: null,
          error: null,
        });

        observer.complete();
      };

      xhr.send();

      return () => {
        if (xhr && xhr.readyState !== XMLHttpRequest.DONE) {
          cancelled = true;
          xhr.abort();
        }

        xhr = null;
      };
    });

    return {
      progress$,
      cancel: () => {
        if (xhr && xhr.readyState !== XMLHttpRequest.DONE) {
          cancelled = true;
          xhr.abort();
        }
      },
    };
  }

  private async resolveFilename(
    url: string,
    xhr: XMLHttpRequest,
    destinationFolder: string,
    options?: FileDownloadOptions
  ): Promise<string> {
    const customFilename = options?.filename?.trim() || null;
    const filenameFromUrl = this.filesystem.getFilenameFromUrl(url);
    const filenameFromMimeType = this.buildFilenameFromMimeType(xhr.response?.type ?? null);

    const rawFilename = customFilename ?? filenameFromUrl ?? filenameFromMimeType ?? 'download';

    const sanitizedFilename = this.filesystem.sanitizeFilename(rawFilename);
    const finalFilename = sanitizedFilename || 'download';

    if (options?.preferUniqueFilename === false) {
      return finalFilename;
    }

    return await this.localFileStore.buildUniqueFilename(destinationFolder, finalFilename);
  }

  private buildFilenameFromMimeType(mimeType: Nullable<string>): Nullable<string> {
    const extension = this.resolveExtensionFromMimeType(mimeType);

    if (!extension) {
      return null;
    }

    return `download.${extension}`;
  }

  private resolveExtensionFromMimeType(mimeType: Nullable<string>): Nullable<string> {
    if (!mimeType) {
      return null;
    }

    const mimeTypeMap: Record<string, string> = {
      'image/jpeg': 'jpg',
      'image/png': 'png',
      'image/gif': 'gif',
      'image/webp': 'webp',
      'image/bmp': 'bmp',
      'application/pdf': 'pdf',
    };

    return mimeTypeMap[mimeType] ?? null;
  }
}
